<template>
  <div class="admin">
    <div class="admin__left" :class="{ cashnote: isCashnote }">
      <div class="admin__version">
        <span>Ver {{ configStore.androidVersion }}</span>
      </div>
      <div class="admin__ip">
        <span v-if="localIP">IP : {{ localIP }}</span
        >&nbsp;&nbsp; <span>Store ID : {{ configStore.config.StoreID }}</span
        >&nbsp;&nbsp;
        <span>Store Code : {{ configStore.config.SubStoreCode }}</span>
      </div>
      <div class="admin__image">
        <template v-if="isCashnote">
          <img v-if="locale === LocaleLanguage.Ko" src="@/assets/images/bg-admin-cashnote-ko.png" alt="관리자 이미지" />
          <img v-else src="@/assets/images/bg-admin-cashnote-en.png" alt="관리자 이미지" />
        </template>
        <template v-else>
          <img v-if="locale === LocaleLanguage.Ko" src="@/assets/images/bg-admin-ko.png" alt="관리자 이미지" />
          <img v-else src="@/assets/images/bg-admin-en.png" alt="관리자 이미지" />
        </template>
      </div>
    </div>
    <div class="admin__right">
      <div v-if="!isCashnote" class="admin__logo"><img src="@/assets/images/imu-ci.svg" alt="아임유 로고" /></div>
      <div class="admin__button-group">
        <div>
          <BaseButton class="button-update" @button-click="handleUpdateClick"
            ><template #icon><img src="@/assets/images/icon-android.svg" /></template
            >{{ t('button.update_check') }}</BaseButton
          >
        </div>
        <div>
          <BaseButton class="button-restart" @button-click="handleRestartClick"
            ><template #icon><img src="@/assets/images/icon-refresh.svg" /></template
            >{{ t('button.program_restart') }}</BaseButton
          >
        </div>
        <div>
          <BaseButton class="button-config" @button-click="handleConfigClick"
            ><template #icon><img src="@/assets/images/icon-config-button.svg" /></template
            >{{ t('button.config') }}</BaseButton
          >
        </div>
        <div>
          <BaseButton class="button-remote-program" @button-click="handleRemoteProgramClick"
            ><template #icon><img src="@/assets/images/icon-remote-program.svg" /></template
            >{{ t('button.remote_program') }}</BaseButton
          >
        </div>
        <div v-if="configStore.config.IsPaymentModeUsed">
          <BaseButton class="button-payment-module" @button-click="handlePaymentModuleClick"
            ><template #icon><img src="@/assets/images/icon-payment-module.svg" /></template
            >{{ t('button.payment_module') }}</BaseButton
          >
        </div>
      </div>
      <div class="admin__close">
        <BaseButton class="button-close" @button-click="handleCloseClick"
          ><template #icon><img src="@/assets/images/icon-close-white.svg" /> </template>
          {{ t('button.close') }}</BaseButton
        >
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import * as Android from '@/api/android';
  import * as Server from '@/api/server';
  import BaseButton from '@/components/base/BaseButton.vue';
  import Config from '@/components/Config.vue';
  import { LocaleLanguage } from '@/enums/LocaleLanguage';
  import UserError from '@/error';
  import { useCommonStore } from '@/stores/common';
  import { useConfigStore } from '@/stores/config';
  import { useModalStore } from '@/stores/modal';
  import { computed, onMounted } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useRouter } from 'vue-router';

  const { t, locale } = useI18n();
  const router = useRouter();
  const configStore = useConfigStore();
  const modalStore = useModalStore();
  const commonStore = useCommonStore();

  const localIP = Android.getIPAddress();

  const isCashnote = computed(() => {
    return configStore.config.ManageCompany === 'KPN' || configStore.config.ManageCompany === 'KCD';
  });

  onMounted(async () => {
    // 필수값 체크
    if (!configStore.config.StoreID || !configStore.config.SubStoreCode) {
      router.replace('/login');
      return;
    }

    // 대기 팝업 타임아웃 해제
    commonStore.clearWaitingPopupInterval();
  });

  const handleUpdateClick = async () => {
    try {
      await Server.checkStatus();
    } catch {
      throw new UserError(t('error.network_not_connect'));
    }

    Android.checkUpdate();
  };

  const handleRestartClick = async () => {
    try {
      await Server.checkStatus();
    } catch {
      throw new UserError(t('error.network_not_connect'));
    }

    Android.reStart();
  };

  const handleConfigClick = async () => {
    try {
      await Server.checkStatus();
    } catch {
      throw new UserError(t('error.network_not_connect'));
    }

    modalStore.openFullScreenModal(Config, {});
  };

  const handleRemoteProgramClick = () => {
    Android.installRemoteSupport();
  };

  const handlePaymentModuleClick = () => {
    Android.installVANAgent();
  };

  const handleCloseClick = () => {
    router.replace('/home');
  };
</script>

<style lang="scss" scoped>
  @use '@/assets/scss/variables' as vars;

  .admin {
    width: 100vw;
    height: 100vh;
    display: flex;
    .admin__left {
      flex: 2;
      position: relative;
      background-color: #303030;
      text-align: center;

      .admin__version {
        position: absolute;
        top: 0;
        left: 4.4776vw;
        background-color: #545454cc;
        border-radius: 0 0 7px 7px;
        padding: 0.7463vw;
        span {
          color: #fff;
          font-size: 1.8657vw;
        }
      }
      .admin__ip {
        position: absolute;
        bottom: 1.4925vw;
        left: 1.4925vw;
        span {
          color: #fff;
          font-size: 1.1194vw;
        }
      }
      .admin__image {
        display: flex;
        align-items: center;
        height: 100%;
        padding: 0 4.4776vw;
        img {
          width: 100%;
        }
      }

      &.cashnote {
        background-color: #fff;
        .admin__ip {
          span {
            color: #000;
          }
        }
      }
    }
    .admin__right {
      flex: 1;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      align-items: center;
      background-color: #292929;
      padding: 2.9851vw;
      .admin__logo {
        width: 100%;
        text-align: right;
      }
      .admin__button-group {
        display: flex;
        flex-direction: column;
        gap: 1.4925vw;
        width: 100%;
        .BaseButton {
          justify-content: start;
          padding-left: 2.9851vw;
        }
        .button-update {
          background-color: #4061a4;
        }
        .button-restart {
          background-color: #009688;
        }
        .button-config {
          background-color: #7f888f;
        }
        .button-remote-program {
          background-color: #9ba0ae;
        }
        .button-payment-module {
          background-color: #637498;
        }
      }
      .admin__close {
        width: 100%;
        .button-close {
          background-color: transparent;
          border: 1px solid #fff;
        }
      }
    }
  }
</style>
